import { getWorkatoStatus } from 'application/Home/Admin/Workato/functions';
import {
    WorkatoActions,
    WorkatoStatus
} from 'application/Home/Admin/Workato/types';
import { TenantApp } from 'application/Shared/types/tenantApp';
import { differenceInHours } from 'date-fns';

import { LicenseDataStatus } from './types';

/**
 * Determines the license data status based on Workato connection status and sync information
 *
 * If the connection is not connected or the app does not have a license recipe, it returns unavailable
 * If the date last synced is more than 24 hours ago, it returns syncFailed
 * Otherwise, it returns available
 *
 * @param workatoStatus - Current status of the Workato connection
 * @param tenantLicenseTier - Array of tenant license tiers
 * @param dateLastSynced - ISO date string of last successful sync
 * @returns LicenseDataStatus indicating the current state
 */
export const getLicenseDataStatus = (
    tenantApp: TenantApp
): LicenseDataStatus => {
    const { tenantLicenseTier, workatoConnection, workatoActions } = tenantApp;
    if (
        !workatoConnection ||
        !workatoActions?.length ||
        !workatoActions?.includes(WorkatoActions.license)
    )
        return LicenseDataStatus.unavailable;

    const workatoStatus = getWorkatoStatus(workatoConnection);

    if (workatoStatus !== WorkatoStatus.connected) {
        return LicenseDataStatus.notConnected;
    }

    if (!tenantLicenseTier?.length) {
        return LicenseDataStatus.syncFailed;
    }

    // Get the max date of the last synced tier
    const dateLastSynced = tenantLicenseTier.reduce(
        (max, tier) => (tier.dateLastSynced > max ? tier.dateLastSynced : max),
        tenantLicenseTier[0]?.dateLastSynced
    );

    const hoursSinceSync = differenceInHours(
        new Date(),
        new Date(dateLastSynced)
    );
    return hoursSinceSync <= 24
        ? LicenseDataStatus.available
        : LicenseDataStatus.syncFailed;
};
